import React, { useState } from 'react';
import { _getFromLocalStorage, _postToLocalStorage } from '../utils/helpers';
import apiRequest from '../utils/api/apiRequest';
import {
  retailNotificationApi,
  retailOrderApi,
  retailStoreApi,
} from '../utils/api/microserviceConfig';
import { useToasts } from 'react-toast-notifications';
import { Analytics } from '../utils/Analytics';

const defaultValue = {
  products: [],
};

export const ProductContext = React.createContext(defaultValue);

export function withProductContext(WrappedComponent) {
  return class withProductContext extends React.Component {
    render() {
      return (
        <ProductContext.Consumer>
          {context => (
            <WrappedComponent productContext={context} {...this.props} />
          )}
        </ProductContext.Consumer>
      );
    }
  };
}

export function ProductProvider(props) {
  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [cartAmounts, setCartAmounts] = useState('');
  const [loadingProductId, setLoadingProductId] = useState(null);
  const [loadingCartItems, setLoadingCartItems] = useState(false);
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [productPageNumber, setProductPageNumber] = useState(1);
  const [loadingViewMore, setLoadingViewMore] = useState(false);
  const [loadingNotifyMe, setLoadingNotifyMe] = useState(null);
  const [minimumOrderableAmount, setMinimumOrderableAmount] = useState(0);

  const [showSearchResult, setShowSearchResult] = useState(false);

  const { addToast } = useToasts();
  const cartId = _getFromLocalStorage('cartId') || 0;

  const getPageSize = () => {
    if (window.screen.width > 800) {
      return 9;
    } else {
      return 10;
    }
  };
  const [filters, setFilters] = useState({
    PageIndex: 1,
    SearchKeyword: '',
    BrandId: '',
    CategoryId: '',
    promotionId: '',
    MinPrice: '',
    MaxPrice: '',
    SortDirection: '',
    SortBy: '',
  });

  const getProducts = async () => {
    const { PageIndex } = filters;

    const getParamsData = () => {
      let str = Object.entries(filters)
        .filter(([, value]) => value)
        .map(([key, val]) => `${key}=${val}`)
        .join('&');
      return str;
    };

    PageIndex === 1 ? setLoadingProducts(true) : setLoadingViewMore(true);

    let productResponse = await apiRequest({
      api: retailStoreApi,
      method: 'get',
      url: `Product?PageSize=${getPageSize()}&${getParamsData()}`,
    });
    setLoadingViewMore(false);

    if (productResponse && !productResponse.error) {
      if (
        productResponse.AllProducts &&
        productResponse.AllProducts.length > 0
      ) {
        //productList = _cloneArray(productResponse.AllProducts);

        setTotalProductCount(productResponse?.Count);
        if (PageIndex > 1) {
          setProducts([...products, ...productResponse.AllProducts]);
        } else {
          setProducts(productResponse.AllProducts);
        }
        //await setProducts(productList);
        //await updateCartProducts(productList);
      } else {
        setProducts([]);
      }
    } else {
      addToast(productResponse.error.message, {
        appearance: 'error',
        autoDismiss: true,
      });

      setProducts([]);
    }
    setLoadingProducts(false);
  };

  const getCartId = cartDetails => {
    if (!cartId || cartId === 0) {
      return _postToLocalStorage('cartId', cartDetails?.[0]?.CartId);
    }
  };

  async function getCartDetails(items) {
    if (items) {
      for (let item of items) {
        let result = await apiRequest({
          api: retailStoreApi,
          method: 'get',
          url: `v1.7/Product?ProductId=${item.ProductId}&StockPointId=${item.StockPointID}&FCLocationID=${item.FCLocationID}`,
        });

        if (result && !result.error) {
          if (result?.Price >= 0) {
            item.HasOrderLimit = result?.HasOrderLimit;
            item.AvailableUnits = result?.AvailableUnits;
            item.Price = item.IsFreeSku ? 0: result?.Price;
            item.HasAvailableStock = !!result?.HasAvailableStock;
            item.PromoPrice = (item.IsFreeSku)? 0: result.PromoPrice;
            item.StockPointID = result?.StockPointID;
          }
        } else {
          item.HasAvailableStock = true;
          item.cartDetailsError = true;
        }
      }
      setCartItems(items);
    }
  }

  const notifyMe = async item => {
    setLoadingNotifyMe({ id: item.ProductId });
    let response = await apiRequest({
      api: retailNotificationApi,
      method: 'post',
      url: `StockNotification/notify-me/${item.ProductId}`,
    });

    if (response && !response.error) {
      addToast('You will be notified when the item is back in stock.', {
        appearance: 'success',
        autoDismiss: true,
      });

      cartItems.forEach(cartItem => {
        let findProduct = cartItem.ProductId === item.ProductId;

        if (findProduct) {
          cartItem.setNotification = true;
        }
      });
      setCartItems(cartItems);
    } else {
      addToast(response.error.message, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
    setLoadingNotifyMe(null);
  };

  async function getCartItems(onPageLoad) {
    if (onPageLoad) {
      setLoadingCartItems(true);
    }
    let result = await apiRequest({
      api: retailOrderApi,
      method: 'get',
      url: 'Cart',
    });
    if (result && !result.error) {
      getCartId(result.CartDetails);
      if (result.CartDetails.length) {
        await getCartDetails(result.CartDetails);
      } else {
        setCartItems([]);
      }
      setCartAmounts(result.CartAmounts);
    }
    setLoadingCartItems(false);
  }

  async function addToCart(item, quantity, logCustomEvent = false) {
    setLoadingProductId({ id: item.ProductId });
    const data = {
      productId: item.ProductId,
      Quantity: quantity ? quantity : 1,
      replace: true,
      StockPointID: item.StockPointID,
      BuyingRate: item.BuyingRate,
      FCLocationID: item.FCLocationID,
    };

    let result = await apiRequest({
      api: retailOrderApi,
      method: 'post',
      url: 'Cart',
      data,
    });

    if (result && !result.error) {
      getCartId(result.Details);
      getCartItems();
      const analyticsData = {
        ProductId: item.ProductId,
        Quantity: quantity ? quantity : 1,
      };

      Analytics.logEvent({
        eventName: 'ADD_TO_CART',
        eventData: analyticsData,
      });
    } else {
      addToast(result.error.message, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
    setLoadingProductId(null);
  }

  async function updateCartQuantity(item, quantity) {
    setLoadingProductId({ id: item.ProductId });

    const data = {
      cartId: cartId,
      ProductId: item.ProductId,
      quantity: quantity,
      replace: true,
    };

    let response = await apiRequest({
      api: retailOrderApi,
      method: 'put',
      url: `Cart/${cartId}`,
      data,
    });
    if (response && !response.error) {
      await getCartItems();
    } else {
      addToast(response.error.message, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
    setLoadingProductId(null);
  }

  async function deleteCartItem(item) {
    setLoadingProductId({ id: item.ProductId });
    let response = await apiRequest({
      api: retailOrderApi,
      method: 'delete',
      url: `cart/${cartId}/product/${item.ProductId}`,
    });

    if (response && response.error) {
      addToast(response.error.message, {
        appearance: 'error',
        autoDismiss: true,
      });
    } else {
      getCartItems();
    }
    setLoadingProductId(null);
  }

  const getMinimumOrderableAmount = async () => {
    let addressData = _getFromLocalStorage('addressData');
    const data = {
      StateId: addressData.StateId,
      CityId: addressData.CityId,
    };
    let response = await apiRequest({
      api: retailOrderApi,
      method: 'post',
      url: `ordersettings/get-minimum-order-amount`,
      data,
    });

    if (response && !response.error) {
      setMinimumOrderableAmount(response);
    } else {
      setMinimumOrderableAmount(20000);
    }
  };

  return (
    <ProductContext.Provider
      value={{
        products: products,
        getProducts: getProducts,
        totalProductCount: totalProductCount,
        loadingProducts: loadingProducts,
        loadingViewMore: loadingViewMore,
        setProductPageNumber: setProductPageNumber,
        productPageNumber: productPageNumber,
        setFilters: setFilters,
        filters: filters,
        addToCart: addToCart,
        deleteCartItem: deleteCartItem,
        getCartItems: getCartItems,
        cartAmounts: cartAmounts,
        cartItems: cartItems,
        loadingCartItems: loadingCartItems,
        updateCartQuantity: updateCartQuantity,
        loadingProductId: loadingProductId,
        searchValue: searchValue,
        setSearchValue: setSearchValue,
        showSearchResult: showSearchResult,
        setShowSearchResult: setShowSearchResult,
        notifyMe: notifyMe,
        loadingNotifyMe: loadingNotifyMe,
        getMinimumOrderableAmount: getMinimumOrderableAmount,
        minimumOrderableAmount: minimumOrderableAmount,
      }}
    >
      {props.children}
    </ProductContext.Provider>
  );
}
